import { Controller } from "@hotwired/stimulus"
import GifService from "../services/gif_service"
import VoiceRecorder from "../services/voice_recorder"

/**
 * Reusable controller for rich text forms (messages, comments)
 * Supports: text input, emoji picker, media upload, voice recording, GIFs
 */
export default class extends Controller {
  static targets = ["textInput", "mediaInput", "mediaPreview", "emojiPicker", "recordBtn", "cameraBtn", "inputArea", "audioInput", "audioRecorder", "recordingTimer", "recordingIndicator", "recordingStopButton", "recordingPaused", "recordingEnded", "submitButton", "emojiTab", "gifTab", "emojiTabContent", "gifTabContent", "gifSearch", "gifResults", "gifLoading", "gifEmpty"]
  static values = {
    maxChars: { type: Number, default: 5000 },
    placeholder: String,
    giphyApiKey: String,
    userLanguage: String,
    sendingText: String,
    enableGif: {type: Boolean, default: false}
  }

  connect() {
    this.selectedFiles = []
    this.currentTab = "emoji"
    this.isSubmitting = false
    this.isStartingRecording = false
    this.voiceRecorder = new VoiceRecorder()

    this._initGiphyConfig()
    if (this.enableGif) {
      this.gifService = new GifService({apiKey: this.giphyApiKey, language: this.userLanguage})
    }

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)

    if (window.visualViewport) {
      this._onViewportResize = this._handleViewportResize.bind(this)
      window.visualViewport.addEventListener("resize", this._onViewportResize)
    }

    this.toggleSendButton()
  }

  disconnect() {
    this.selectedFiles.forEach(file => {
      if (file.preview) URL.revokeObjectURL(file.preview)
    })
    this.recordingAborted = true
    if (this.voiceRecorder.isRecording) this.voiceRecorder.cancel()
    this._clearOptimisticVoiceBubble()
    document.removeEventListener("click", this.handleClickOutside)
    if (window.visualViewport && this._onViewportResize) {
      window.visualViewport.removeEventListener("resize", this._onViewportResize)
    }
  }

  // ===== Config =====

  _initGiphyConfig() {
    if (this.hasEmojiPickerTarget) {
      this.giphyApiKey = this.emojiPickerTarget.dataset.richFormGiphyApiKeyValue || this.giphyApiKeyValue || document.querySelector('meta[name="giphy-api-key"]')?.content || ""
      this.userLanguage = this.emojiPickerTarget.dataset.richFormUserLanguageValue || this.userLanguageValue || "en"
      this.enableGif = this.emojiPickerTarget.dataset.richFormEnableGifValue === "true" || this.enableGifValue
    } else {
      this.giphyApiKey = this.giphyApiKeyValue || document.querySelector('meta[name="giphy-api-key"]')?.content || ""
      this.userLanguage = this.userLanguageValue || "en"
      this.enableGif = this.enableGifValue
    }
  }

  // ===== Emoji Picker =====

  handleClickOutside(event) {
    if (this.hasEmojiPickerTarget && !this.emojiPickerTarget.classList.contains("hidden")) {
      const picker = this.emojiPickerTarget
      const button = this.element.querySelector('[data-action*="toggleEmojiPicker"]')
      if (!picker.contains(event.target) && !button?.contains(event.target)) {
        picker.classList.add("hidden")
      }
    }
  }

  toggleEmojiPicker(event) {
    event.preventDefault()
    event.stopPropagation()
    this.closeAllDropdowns()

    if (this.hasEmojiPickerTarget) {
      const isHidden = this.emojiPickerTarget.classList.contains("hidden")
      this.emojiPickerTarget.classList.toggle("hidden")
      if (isHidden) this.switchToTab("emoji")
    }
  }

  switchTab(event) {
    event.preventDefault()
    event.stopPropagation()
    this.switchToTab(event.currentTarget.dataset.tab)
  }

  switchToTab(tab) {
    this.currentTab = tab
    if (!this.enableGif && tab === "gif") return

    const activeClasses = ["text-sky-600", "dark:text-sky-400", "border-b-2", "border-sky-600", "dark:border-sky-400"]
    const inactiveClasses = ["text-zinc-500", "dark:text-zinc-400"]

    if (this.hasEmojiTabTarget) {
      this.emojiTabTarget.classList.toggle("text-sky-600", tab === "emoji")
      if (tab === "emoji") {
        this.emojiTabTarget.classList.add(...activeClasses)
        this.emojiTabTarget.classList.remove(...inactiveClasses)
      } else {
        this.emojiTabTarget.classList.remove(...activeClasses)
        this.emojiTabTarget.classList.add(...inactiveClasses)
      }
    }

    if (this.enableGif && this.hasGifTabTarget) {
      if (tab === "gif") {
        this.gifTabTarget.classList.add(...activeClasses)
        this.gifTabTarget.classList.remove(...inactiveClasses)
      } else {
        this.gifTabTarget.classList.remove(...activeClasses)
        this.gifTabTarget.classList.add(...inactiveClasses)
      }
    }

    if (this.hasEmojiTabContentTarget) {
      this.emojiTabContentTarget.classList.toggle("hidden", tab !== "emoji")
    }

    if (this.enableGif && this.hasGifTabContentTarget) {
      this.gifTabContentTarget.classList.toggle("hidden", tab !== "gif")
      if (tab === "gif" && this.hasGifSearchTarget && !this.gifSearchTarget.value.trim()) {
        this._loadTrendingGifs()
      }
    }
  }

  closeAllDropdowns() {
    document.querySelectorAll(".hs-dropdown").forEach(dropdown => {
      const instance = window.HSDropdown?.getInstance(dropdown)
      if (instance) {
        instance.close()
      } else {
        dropdown.querySelector(".hs-dropdown-menu")?.classList.add("hidden", "opacity-0")
        dropdown.querySelector(".hs-dropdown-menu")?.classList.remove("opacity-100")
        dropdown.querySelector(".hs-dropdown-toggle")?.setAttribute("aria-expanded", "false")
        dropdown.classList.remove("open")
      }
    })

    document.querySelectorAll("[data-rich-form-target='emojiPicker']").forEach(picker => {
      if (picker !== this.emojiPickerTarget) picker.classList.add("hidden")
    })
  }

  // Keeps the picker open so several emojis can be chosen in a row
  insertEmoji(event) {
    event.preventDefault()
    this._insertText(event.currentTarget.dataset.emoji)
  }

  // ===== GIF Handling (delegated to GifService) =====

  insertGif(event) {
    event.preventDefault()
    const gifUrl = event.currentTarget.dataset.gifUrl
    if (!gifUrl) return

    if (this.enableGif && this.hasMediaInputTarget) {
      this._downloadAndSubmitGif(gifUrl)
    } else {
      this._insertText(gifUrl)
      this._hideEmojiPicker()
    }
  }

  async _downloadAndSubmitGif(gifUrl) {
    try {
      this._hideEmojiPicker()

      const file = await this.gifService.downloadAsFile(gifUrl)
      if (this.hasMediaInputTarget) {
        const dt = new DataTransfer()
        dt.items.add(file)
        this.mediaInputTarget.files = dt.files
        this.element.requestSubmit()
      } else {
        this._insertText(gifUrl)
      }
    } catch (error) {
      console.error("Error downloading GIF:", error)
      this._insertText(gifUrl)
    }
  }

  searchGifs(event) {
    this.gifService.debouncedSearch(
        event.target.value.trim(),
        (gifs) => this._displayGifResults(gifs)
    )
  }

  async _loadTrendingGifs() {
    this._showGifLoading()
    const gifs = await this.gifService.trending()
    this._hideGifLoading()
    this._displayGifResults(gifs)
  }

  _displayGifResults(gifs) {
    if (!this.hasGifResultsTarget) return
    if (gifs.length === 0) {
      this._showGifEmpty()
      return
    }
    this._hideGifEmpty()
    this.gifService.renderGifs(gifs, this.gifResultsTarget, "click->rich-form#insertGif")
  }

  _showGifLoading() {
    if (this.hasGifLoadingTarget) this.gifLoadingTarget.classList.remove("hidden")
    if (this.hasGifResultsTarget) this.gifResultsTarget.classList.add("hidden")
  }

  _hideGifLoading() {
    if (this.hasGifLoadingTarget) this.gifLoadingTarget.classList.add("hidden")
    if (this.hasGifResultsTarget) this.gifResultsTarget.classList.remove("hidden")
  }

  _showGifEmpty() {
    if (this.hasGifEmptyTarget) this.gifEmptyTarget.classList.remove("hidden")
  }

  _hideGifEmpty() {
    if (this.hasGifEmptyTarget) this.gifEmptyTarget.classList.add("hidden")
  }

  // ===== Text Insertion =====

  _insertText(text) {
    const input = this.textInputTarget
    const start = input.selectionStart
    const end = input.selectionEnd
    input.value = input.value.substring(0, start) + text + input.value.substring(end)
    input.selectionStart = input.selectionEnd = start + text.length
    input.focus()
    input.dispatchEvent(new Event("input", {bubbles: true}))
  }

  _hideEmojiPicker() {
    if (this.hasEmojiPickerTarget) this.emojiPickerTarget.classList.add("hidden")
  }

  // ===== Media Handling =====

  previewMedia(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return
    this.selectedFiles = [...this.selectedFiles, ...files]
    this._updateMediaPreview()
    this._updateMediaInput()
    this.toggleSendButton()
  }

  // Photo or video taken with the in-chat camera (camera-capture controller): it is sent right
  // away with its caption, which replaces the draft (the camera pre-filled it with the draft).
  sendCapturedMedia(event) {
    const {file, caption} = event.detail || {}
    if (!file || !this.hasMediaInputTarget || this.isSubmitting) return

    this.selectedFiles = [...this.selectedFiles, file]
    this._updateMediaPreview()
    this._updateMediaInput()

    if (this.hasTextInputTarget) {
      this.textInputTarget.value = caption || ""
      this._resizeTextarea(this.textInputTarget)
    }
    this.toggleSendButton()
    this.element.requestSubmit()
  }

  removeMedia(index) {
    if (this.selectedFiles[index]?.preview) URL.revokeObjectURL(this.selectedFiles[index].preview)
    this.selectedFiles.splice(index, 1)
    this._updateMediaPreview()
    this._updateMediaInput()
    this.toggleSendButton()
  }

  // ===== Send / voice button =====

  // Like WhatsApp: an empty composer shows the voice message button; as soon as there is
  // text or a media to send it is replaced by the send button. Forms without a voice button
  // (comments…) keep their send button as is.
  toggleSendButton() {
    if (!this.hasRecordBtnTarget || !this.hasSubmitButtonTarget) return

    const hasText = this.hasTextInputTarget && this.textInputTarget.value.trim().length > 0
    const canSend = hasText || this.selectedFiles.length > 0
    this.submitButtonTarget.classList.toggle("hidden", !canSend)
    this.recordBtnTarget.classList.toggle("hidden", canSend)
  }

  _updateMediaPreview() {
    if (!this.hasMediaPreviewTarget) return

    if (this.selectedFiles.length === 0) {
      this.mediaPreviewTarget.classList.add("hidden")
      return
    }

    this.mediaPreviewTarget.classList.remove("hidden")
    const container = document.createElement("div")
    container.className = "flex flex-wrap gap-2"

    this.selectedFiles.forEach((file, index) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative"

      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        if (!file.preview) {
          file.preview = URL.createObjectURL(file)
        }
        if (file.type.startsWith("image/")) {
          const img = document.createElement("img")
          img.className = "w-20 h-20 object-cover rounded-lg"
          img.src = file.preview
          wrapper.appendChild(img)
        } else {
          const video = document.createElement("video")
          video.className = "w-20 h-20 object-cover rounded-lg"
          video.src = file.preview
          wrapper.appendChild(video)
        }
      }

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
      removeBtn.innerHTML = `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>`
      removeBtn.onclick = (e) => {
        e.preventDefault()
        this.removeMedia(index)
      }
      wrapper.appendChild(removeBtn)

      container.appendChild(wrapper)
    })

    this.mediaPreviewTarget.innerHTML = ""
    this.mediaPreviewTarget.appendChild(container)
  }

  _updateMediaInput() {
    if (!this.hasMediaInputTarget) return
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.mediaInputTarget.files = dt.files
  }

  // ===== Voice Recording (delegated to VoiceRecorder) =====

  // The recording panel opens on the tap, before the microphone is ready: asking for the
  // permission and opening the microphone (getUserMedia, Flutter bridge) can take a second or
  // more on mobile. The panel stays in a "preparing" state (grey dot, send disabled) until then.
  async startRecording(event) {
    if (event) event.preventDefault()
    if (this.isStartingRecording || this.voiceRecorder.isRecording) return

    this.isStartingRecording = true
    this.recordingAborted = false
    this._showRecorderPanel()
    this._setRecorderPreparing(true)

    let started = false
    try {
      started = await this.voiceRecorder.start({
        onTimerUpdate: (time) => {
          if (this.hasRecordingTimerTarget) this.recordingTimerTarget.textContent = time
        },
        onInterrupted: ({resumable}) => this._showRecordingInterrupted(resumable),
        onResumed: () => this._hideRecordingInterrupted()
      })
    } finally {
      this.isStartingRecording = false
    }

    // Cancelled (or the form went away) while the microphone was opening.
    if (this.recordingAborted) {
      if (started) this.voiceRecorder.cancel()
      return
    }

    if (!started) {
      this._hideRecorderPanel()
      alert("Impossible d'accéder au microphone. Vérifiez les permissions.")
      return
    }

    this._setRecorderPreparing(false)
    this.element.dispatchEvent(new CustomEvent("recording:started", {bubbles: true}))
  }

  _showRecorderPanel() {
    if (!this.hasAudioRecorderTarget) return
    if (this.hasRecordingTimerTarget) this.recordingTimerTarget.textContent = "00:00"
    this.audioRecorderTarget.classList.remove("hidden")
    if (this.hasInputAreaTarget) this.inputAreaTarget.classList.add("hidden")
  }

  _hideRecorderPanel() {
    if (!this.hasAudioRecorderTarget) return
    this.audioRecorderTarget.classList.add("hidden")
    if (this.hasInputAreaTarget) this.inputAreaTarget.classList.remove("hidden")
  }

  _setRecorderPreparing(preparing) {
    if (this.hasRecordingStopButtonTarget) this.recordingStopButtonTarget.disabled = preparing
    if (this.hasRecordingIndicatorTarget) {
      this.recordingIndicatorTarget.classList.toggle("bg-zinc-400", preparing)
      this.recordingIndicatorTarget.classList.toggle("bg-red-500", !preparing)
      this.recordingIndicatorTarget.classList.toggle("animate-pulse", !preparing)
    }
  }

  async stopRecording(event) {
    if (event) event.preventDefault()

    this._hideRecordingInterrupted()

    const audioFile = await this.voiceRecorder.stop()
    this.element.dispatchEvent(new CustomEvent("recording:stopped", {bubbles: true}))

    this._hideRecorderPanel()

    if (audioFile && this.hasAudioInputTarget && !this.isSubmitting) {
      const dt = new DataTransfer()
      dt.items.add(audioFile)
      this.audioInputTarget.files = dt.files
      // Optimistic UI: show the voice bubble immediately so the upload (which is
      // network-bound and can take several seconds on mobile) doesn't look like
      // nothing happened. The server's turbo-stream append replaces it on success.
      this._showOptimisticVoiceBubble(audioFile)
      this.element.requestSubmit()
    }
  }

  // ===== Submission state (prevents double-send while the upload is in flight) =====

  submitStart() {
    this.isSubmitting = true
    this._setSubmittingUI(true)
  }

  submitEnd() {
    this.isSubmitting = false
    this._setSubmittingUI(false)
    // On success the real message has already been appended by the turbo-stream
    // response; on failure nothing was sent. Either way, drop the placeholder.
    this._clearOptimisticVoiceBubble()
  }

  _setSubmittingUI(busy) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = busy
    if (this.hasRecordBtnTarget) this.recordBtnTarget.disabled = busy
    if (this.hasCameraBtnTarget) this.cameraBtnTarget.disabled = busy
  }

  _showOptimisticVoiceBubble(file) {
    const list = document.getElementById("messages-list")
    if (!list) return
    this._clearOptimisticVoiceBubble()

    this.pendingVoiceUrl = URL.createObjectURL(file)
    const label = this.sendingTextValue || "Envoi…"

    const wrapper = document.createElement("div")
    wrapper.id = "voice-optimistic-pending"
    wrapper.className = "mb-4 text-right select-none opacity-60"
    wrapper.innerHTML = `
      <div class="inline-flex items-center gap-2.5 max-w-[280px] px-3 py-2 rounded-lg bg-sky-500 dark:bg-sky-600 shadow-sm">
        <span class="inline-block size-4 rounded-full border-2 border-white/70 border-t-transparent animate-spin"></span>
        <span class="text-xs text-white/90">${label}</span>
      </div>`

    list.appendChild(wrapper)
    this.pendingVoiceEl = wrapper
    wrapper.scrollIntoView({block: "end"})
  }

  _clearOptimisticVoiceBubble() {
    if (this.pendingVoiceEl) {
      this.pendingVoiceEl.remove()
      this.pendingVoiceEl = null
    }
    if (this.pendingVoiceUrl) {
      URL.revokeObjectURL(this.pendingVoiceUrl)
      this.pendingVoiceUrl = null
    }
  }

  cancelRecording(event) {
    if (event) event.preventDefault()

    // Still opening the microphone: startRecording drops the recorder once it resolves.
    if (this.isStartingRecording) this.recordingAborted = true
    this.voiceRecorder.cancel()
    this._hideRecordingInterrupted()
    this.element.dispatchEvent(new CustomEvent("recording:stopped", {bubbles: true}))

    if (this.hasAudioInputTarget) this.audioInputTarget.value = ""
    this._hideRecorderPanel()
  }

  // The microphone was cut off by the OS: the recording is paused (resumable) or stopped
  // for good, and the sender is told so instead of unknowingly sending silence.
  _showRecordingInterrupted(resumable) {
    if (this.hasRecordingIndicatorTarget) this.recordingIndicatorTarget.classList.remove("animate-pulse", "bg-red-500")
    if (this.hasRecordingIndicatorTarget) this.recordingIndicatorTarget.classList.add("bg-zinc-400")
    if (this.hasRecordingPausedTarget) this.recordingPausedTarget.classList.toggle("hidden", !resumable)
    if (this.hasRecordingEndedTarget) this.recordingEndedTarget.classList.toggle("hidden", resumable)
  }

  _hideRecordingInterrupted() {
    if (this.hasRecordingIndicatorTarget) this.recordingIndicatorTarget.classList.remove("bg-zinc-400")
    if (this.hasRecordingIndicatorTarget) this.recordingIndicatorTarget.classList.add("animate-pulse", "bg-red-500")
    if (this.hasRecordingPausedTarget) this.recordingPausedTarget.classList.add("hidden")
    if (this.hasRecordingEndedTarget) this.recordingEndedTarget.classList.add("hidden")
  }

  // ===== Keyboard & Resize =====

  handleKeydown(event) {
    // Enter adds a newline; only the send button submits the form.
    if (event.key === "Enter" && !event.shiftKey) {
      event.stopPropagation()
      // Let the textarea insert the newline naturally — no requestSubmit().
    }
  }

  adjustHeight(event) {
    this._resizeTextarea(event.target)
  }

  _resizeTextarea(textarea) {
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10) || 20
    const maxHeight = lineHeight * 6

    textarea.style.height = "auto"
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = newHeight + "px"
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden"
  }

  // Keep the focused input above the software keyboard on mobile / WebView.
  _handleViewportResize() {
    if (!this.hasTextInputTarget) return
    if (document.activeElement !== this.textInputTarget) return
    requestAnimationFrame(() => {
      this.textInputTarget.scrollIntoView({behavior: "smooth", block: "nearest"})
    })
  }

  // ===== Form Reset =====

  resetForm(event) {
    if (event.detail && event.detail.success) {
      this.textInputTarget.value = ""
      this.textInputTarget.style.height = "auto"
      this.textInputTarget.style.overflowY = "hidden"
      this.selectedFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview)
      })
      this.selectedFiles = []
      if (this.hasMediaPreviewTarget) {
        this.mediaPreviewTarget.classList.add("hidden");
        this.mediaPreviewTarget.innerHTML = ""
      }
      if (this.hasMediaInputTarget) {
        this.mediaInputTarget.value = "";
        this.mediaInputTarget.files = new DataTransfer().files
      }
      if (this.hasAudioInputTarget) {
        this.audioInputTarget.value = "";
        this.audioInputTarget.files = new DataTransfer().files
      }
      this._hideEmojiPicker()
      this.toggleSendButton()
    }
  }
}
